import React from "react";
import { CommentNode } from "./types";
import CommentComposer from "./CommentComposer";
import {
  formatCommentTime,
  getCommentDisplayExcerpt,
  getCommentScopeClass,
  getCommentScopeLabel,
  isModeratorRole,
} from "./commentText";

interface CommentInspectorProps {
  selectedComment: CommentNode | null;
  byId: Map<number, CommentNode>;
  currentEditId?: number | null;
  currentUserId?: number;
  currentUserRole?: string;
  onSelect: (commentId: number) => void;
  onReply?: (commentId: number, content: string) => Promise<void>;
  replyUnavailableMessage?: string;
  onSoftRemove?: (commentId: number) => Promise<void>;
  onHardDeleteThread?: (commentId: number) => Promise<void>;
}

const CommentInspector: React.FC<CommentInspectorProps> = ({
  selectedComment,
  byId,
  currentEditId,
  currentUserId,
  currentUserRole,
  onSelect,
  onReply,
  replyUnavailableMessage,
  onSoftRemove,
  onHardDeleteThread,
}) => {
  if (!selectedComment) {
    return (
      <aside className="comment-explorer-inspector">
        <div className="comment-explorer-inspector-empty">
          <h3>No comment selected</h3>
          <p>Select a thread, reply, or recent activity item to inspect it here.</p>
        </div>
      </aside>
    );
  }

  const parent = selectedComment.parentId ? byId.get(selectedComment.parentId) ?? null : null;
  const scopeClass = getCommentScopeClass(selectedComment, currentEditId);
  const canModerate = isModeratorRole(currentUserRole);
  const canSoftRemove = Boolean(
    onSoftRemove && !selectedComment.isRemoved && (canModerate || currentUserId === selectedComment.userId)
  );
  const canHardDelete = Boolean(onHardDeleteThread && canModerate);

  const handleSoftRemove = async () => {
    if (!onSoftRemove) return;
    if (!window.confirm("Soft-remove this comment? Child replies will stay visible.")) return;
    await onSoftRemove(selectedComment.id);
  };

  const handleHardDelete = async () => {
    if (!onHardDeleteThread) return;
    if (!window.confirm("Permanently delete this full comment thread? This cannot be undone.")) return;
    await onHardDeleteThread(selectedComment.id);
  };

  return (
    <aside className="comment-explorer-inspector comment-explorer-action-drawer">
      <div className="comment-explorer-inspector-head">
        <div>
          <span className={`comment-explorer-scope-badge ${scopeClass}`}>
            {getCommentScopeLabel(selectedComment, currentEditId)}
          </span>
          <h3>Selected comment actions · {selectedComment.isRemoved ? "Removed comment" : getCommentDisplayExcerpt(selectedComment, 72)}</h3>
        </div>
      </div>

      <div className="comment-explorer-inspector-stats">
        <div><span>Author</span><strong>{selectedComment.user?.name || "Unknown"}</strong></div>
        <div><span>Created</span><strong>{formatCommentTime(selectedComment.createdAt)}</strong></div>
        <div><span>Direct</span><strong>{selectedComment.directReplyCount}</strong></div>
        <div><span>Total</span><strong>{selectedComment.totalReplyCount}</strong></div>
      </div>

      {parent && (
        <button type="button" className="comment-explorer-parent-link" onClick={() => onSelect(parent.id)}>
          ↑ Parent: {getCommentDisplayExcerpt(parent, 86)}
        </button>
      )}

      <div className="comment-explorer-inspector-hint">
        The selected card is expanded in the active view. Use this drawer only for parent navigation, reply, and moderation actions.
      </div>

      {onReply ? (
        <CommentComposer
          label="Reply to this comment"
          placeholder="Write a focused reply to the selected comment…"
          onSubmit={(content) => onReply(selectedComment.id, content)}
        />
      ) : (
        <div className="comment-explorer-login-note">
          {replyUnavailableMessage || "Log in to reply to this comment."}
        </div>
      )}

      {(canSoftRemove || canHardDelete) && (
        <div className="comment-explorer-moderation-actions">
          {canSoftRemove && (
            <button type="button" onClick={handleSoftRemove}>
              Soft remove
            </button>
          )}
          {canHardDelete && (
            <button type="button" className="is-danger" onClick={handleHardDelete}>
              Hard delete thread
            </button>
          )}
        </div>
      )}
    </aside>
  );
};

export default CommentInspector;
